import { useMutation } from "@tanstack/react-query"
import { mergeDeep } from "remeda"
import { ERROR_CODE } from "@/constants/errorCode"
import { hasElectricity, hasGas } from "@/features/subscription"
import { computeInvoiceSavingResult } from "@/features/subscription/quotes/utils/productsBundleCardComparison"
import { OEBackend, type VariablesOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { sortQuotedProductByPrice } from "../../quotes/utils/sortQuotedProductByPrice"
import { isCustomizableQuote, isFastQuoteJourney } from "../../utils/onboardingVariant"
import { type OnboardingFormValues, type OnboardingState, useOnboardingToStep } from "../../wizard"
import { type RequestQuoteMutationInput, useRequestQuoteMutation } from "./useRequestQuoteMutation"

const AnnotateInvoiceExtractionMutation = OEBackend.graphql(`
    mutation AnnotateInvoiceExtraction($extractionUuid: UUID!, $krakenQuotedProductId: Int) {
        annotateInvoiceExtraction(
            extractionUuid: $extractionUuid
            krakenQuotedProductId: $krakenQuotedProductId
        ) {
            invoiceAnnotated
        }
    }
`)

type ElectricityQuoteInput = NonNullable<RequestQuoteMutationInput["electricityQuotes"]>[number]
type GasQuoteInput = NonNullable<RequestQuoteMutationInput["gasQuotes"]>[number]

type AnnualConsumptionProps = {
    electricity?: NonNullable<ElectricityQuoteInput["annualConsumption"]>
    gas?: NonNullable<GasQuoteInput["providedAnnualConsumption"]>
}

type FormatFormValuesToRequestQuoteInputProps = {
    formValues: OnboardingFormValues
    annualConsumption?: AnnualConsumptionProps
    powerOptimizationEnabled?: boolean
    forceUserInputPower?: boolean
    multiProviderCalendarQuoting: boolean
    useRecommendedPower?: boolean
}

function getSubscribedMaxPower(props: FormatFormValuesToRequestQuoteInputProps) {
    // There is a condition in Kraken that prevents PRICE_COMPARISON to use subscribedMaxPower input from requestQuote
    if (props.formValues.affiliateOrganisationSalesChannel === "PRICE_COMPARISON") {
        return
    }

    if (props.forceUserInputPower) {
        return props.formValues.userInput.electricitySubscribedMaxPower
    }

    if (props.useRecommendedPower) {
        return props.formValues.meterPowerAnalysis.recommendedSubscribedMaxPower
    }

    if (props.powerOptimizationEnabled) {
        return props.formValues.userInput.electricitySubscribedMaxPower
    }
}

function formatFormValuesToRequestQuoteInput(props: FormatFormValuesToRequestQuoteInputProps) {
    const { formValues, annualConsumption } = props

    const input: RequestQuoteMutationInput = {
        affiliateSessionId: formValues.affiliateSessionId,
        pmaxAnalysisToken:
            formValues.journeyType === "CHANGE_OF_SUPPLIER" ? formValues.pmaxAnalysisToken : null,
        energyLandscapeId: formValues.energyLandscapeId,
    }

    if (hasElectricity(formValues.selectedEnergyTypes)) {
        if (!formValues.electricity.supplyPoint.id) {
            throw new Error(ERROR_CODE.UNKNOWN_ERROR)
        }

        const electricityQuoteInput: ElectricityQuoteInput = {
            supplyPointId: formValues.electricity.supplyPoint.id,
        }

        if (!props.multiProviderCalendarQuoting) {
            electricityQuoteInput.providerCalendarId = formValues.electricity.providerCalendarId
        }

        const subscribedMaxPower = getSubscribedMaxPower(props)

        if (subscribedMaxPower) {
            electricityQuoteInput.subscribedMaxPower = subscribedMaxPower
        }

        if (annualConsumption?.electricity) {
            electricityQuoteInput.annualConsumption = annualConsumption.electricity
        }

        input.electricityQuotes = [electricityQuoteInput]
    }

    if (hasGas(formValues.selectedEnergyTypes)) {
        if (!formValues.gas.supplyPoint.id) {
            throw new Error(ERROR_CODE.UNKNOWN_ERROR)
        }

        input.gasQuotes = [
            {
                supplyPointId: formValues.gas.supplyPoint.id,
                providedAnnualConsumption: annualConsumption?.gas,
            },
        ]
    }

    return input
}

type UseRequestQuoteProps = {
    stepName: OnboardingRouteNames
}

export function useRequestQuote({ stepName }: UseRequestQuoteProps) {
    const { toStep } = useOnboardingToStep(stepName)
    const oeGraphQLClient = useGraphQLClient({ target: "oeBackend" })

    const requestQuote = useRequestQuoteMutation()

    const annotateInvoiceExtraction = useMutation({
        mutationKey: ["annotateInvoiceExtraction"],
        mutationFn: async (input: VariablesOf<typeof AnnotateInvoiceExtractionMutation>) =>
            await oeGraphQLClient.request(AnnotateInvoiceExtractionMutation, input),
    })

    const handleRequestQuote = async (props: {
        onboardingState: OnboardingState
        providedAnnualConsumption?: AnnualConsumptionProps
        annualConsumptionFallback?: AnnualConsumptionProps
        forceUserInputPower?: boolean
    }) => {
        let quoteResult = await requestQuote.mutateAsync(
            formatFormValuesToRequestQuoteInput({
                formValues: props.onboardingState.formValues,
                annualConsumption: props.providedAnnualConsumption,
                powerOptimizationEnabled: !isFastQuoteJourney(props.onboardingState.formValues),
                multiProviderCalendarQuoting: isCustomizableQuote(props.onboardingState.formValues),
                forceUserInputPower: props.forceUserInputPower,
            })
        )

        if (quoteResult.quoteRequestCode === null) {
            if (
                !props.annualConsumptionFallback?.electricity &&
                !props.annualConsumptionFallback?.gas
            ) {
                return toStep(OnboardingRouteNames.EnergyLandscape, props.onboardingState)
            }

            // Retry with annual consumption if available
            quoteResult = await requestQuote.mutateAsync(
                formatFormValuesToRequestQuoteInput({
                    formValues: props.onboardingState.formValues,
                    annualConsumption: props.annualConsumptionFallback,
                    powerOptimizationEnabled: !isFastQuoteJourney(props.onboardingState.formValues),
                    multiProviderCalendarQuoting: isCustomizableQuote(
                        props.onboardingState.formValues
                    ),
                    forceUserInputPower: props.forceUserInputPower,
                })
            )

            if (quoteResult.quoteRequestCode === null) {
                return toStep(OnboardingRouteNames.EnergyLandscape, props.onboardingState)
            }
        }

        const { quoteRequestCode, electricityQuotedSupplyPoint, gasQuotedSupplyPoint } = quoteResult

        const newState = mergeDeep(structuredClone(props.onboardingState), {
            formValues: {
                quoteCode: quoteRequestCode,
            },
        })

        if (hasElectricity(newState.formValues.selectedEnergyTypes)) {
            if (!electricityQuotedSupplyPoint) {
                return toStep(OnboardingRouteNames.EnergyLandscape, props.onboardingState)
            }

            if (newState.formValues.electricity.invoiceUpload.extractionUuid) {
                await annotateInvoiceExtraction.mutateAsync({
                    extractionUuid: newState.formValues.electricity.invoiceUpload.extractionUuid,
                    krakenQuotedProductId:
                        electricityQuotedSupplyPoint.quotedProducts.toSorted(
                            sortQuotedProductByPrice
                        )[0].id,
                })

                if (newState.formValues.electricity.invoiceUpload.amountDue) {
                    const { yearlySaving, isSignificant } = computeInvoiceSavingResult(
                        electricityQuotedSupplyPoint.quotedProducts,
                        newState.formValues.electricity.invoiceUpload.amountDue
                    )

                    newState.formValues.electricity.yearlySaving = yearlySaving
                    newState.formValues.electricity.isSignificant = isSignificant
                }
            }
        }

        if (hasGas(newState.formValues.selectedEnergyTypes)) {
            if (!gasQuotedSupplyPoint) {
                return toStep(OnboardingRouteNames.EnergyLandscape, props.onboardingState)
            }

            if (newState.formValues.gas.invoiceUpload.extractionUuid) {
                await annotateInvoiceExtraction.mutateAsync({
                    extractionUuid: newState.formValues.gas.invoiceUpload.extractionUuid,
                    krakenQuotedProductId:
                        gasQuotedSupplyPoint.quotedProducts.toSorted(sortQuotedProductByPrice)[0]
                            .id,
                })
            }
        }

        if (
            hasElectricity(newState.formValues.selectedEnergyTypes) &&
            props.onboardingState.formValues.meterPowerAnalysis.recommendedSubscribedMaxPower &&
            isCustomizableQuote(props.onboardingState.formValues)
        ) {
            const { quoteRequestCode } = await requestQuote.mutateAsync(
                formatFormValuesToRequestQuoteInput({
                    formValues: props.onboardingState.formValues,
                    annualConsumption: props.providedAnnualConsumption,
                    powerOptimizationEnabled: true,
                    multiProviderCalendarQuoting: true,
                    useRecommendedPower: true,
                })
            )

            newState.formValues.optimizedQuoteCode = quoteRequestCode
        } else {
            newState.formValues.optimizedQuoteCode = null
        }

        await toStep(OnboardingRouteNames.Quote, newState)
    }

    return [handleRequestQuote, requestQuote] as const
}
